import type {
	AssetsConfig,
	AssetsConfigAllAtlassianArgs,
	AssetsConfigPreloadedArgs,
} from '@atlaskit/react-ufo/common';
import {
	init,
	type GenericAnalyticWebClientPromise,
} from '@atlaskit/react-ufo/interaction-metrics-init';
import { configure as configureResourceTimings } from '@atlaskit/react-ufo/resource-timing/config';
import { startResourceTimingBuffer } from '@atlaskit/react-ufo/resource-timing/utils';
import { configure as configureSsrTiming } from '@atlaskit/react-ufo/ssr';
import { type VCRevisionDebugDetails } from '@atlaskit/react-ufo/vc';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getLayoutShiftWithAttribution } from '@confluence/browser-metrics/entry-points/getLayoutShiftWithAttribution.ts';
import { fg } from '@confluence/feature-gating';
import { getReactHydrationStats } from '@confluence/hydration-utils/entry-points/getReactHydrationStats.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
import type { SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { additionalPayloadData as getAdditionalPayloadData } from './additionalPayloadData.ts';
import { getSSRDoneTime, getSSRTimings } from './ssr.ts';
import { initUFOTrace } from './ufoTrace.ts';
import { resourceTimingsConfig } from './resource-timings.ts';

// TODO: AFO-4167: Remove fallback once the Statsig Dynamic Config for UFO config is available for FedRamp environment
export const FALLBACK_UFO_CONFIG: {
	typingMethod: string;
	kind: {
		hover: number;
		legacy: number;
		typing: number;
		press: number;
		page_load: number;
		transition: number;
		generic: number;
	};
	rates: {
		unknown: number;
	};
	removeInteractionsUFOPrefixes: boolean;
	enableEditorLnvHandler: boolean;
	autoGeneratedRate: number;
	rules: never[];
	awaitBM3TTI: never[];
	ufoNameOverrides: {};
	killswitch: never[];
	segmentPrefix: string;
	enabled: boolean;
	doNotAbortActivePressInteraction: never[];
	doNotAbortActivePressInteractionOnTransition: never[];
	enableCompleteUsingDoubleRAF: boolean;
	capability: {
		feature_flag_access: number;
		react_profiler: number;
	};
	namePrefix: string;
	removePageSegmentsUFOPrefixes: boolean;
} = {
	typingMethod: 'timeout',
	kind: { hover: 0, legacy: 0, typing: 0, press: 1, page_load: 1, transition: 1, generic: 0 },
	rates: { unknown: 1000 },
	removeInteractionsUFOPrefixes: true,
	enableEditorLnvHandler: true,
	autoGeneratedRate: 1000,
	rules: [],
	awaitBM3TTI: [],
	ufoNameOverrides: {},
	killswitch: [],
	segmentPrefix: 'ufo-',
	enabled: true,
	doNotAbortActivePressInteraction: [],
	doNotAbortActivePressInteractionOnTransition: [],
	enableCompleteUsingDoubleRAF: true,
	capability: { feature_flag_access: 1, react_profiler: 1 },
	namePrefix: 'ufo-',
	removePageSegmentsUFOPrefixes: false,
};

const getConfig = (configName: string): any => {
	try {
		return JSON.parse(StatsigConfigurations.getString(configName, ''));
	} catch {
		// TODO: AFO-4167: Change the fallback to be `{}` once we have a stable config for FedRamp environment
		return FALLBACK_UFO_CONFIG;
	}
};

function runConfigureSsrTiming() {
	configureSsrTiming({
		getDoneMark: () => getMark('CFP-63.ssr-ttr') ?? null, // from confluence/next/packages/browser-metrics/src/initializeBrowserMetrics.ts
		// TODO: add correct functions to `getTimings` and `getFeatureFlags`
		getTimings: () => null,
		getFeatureFlags: () => null,
	});
}

export function initializeUFO({ shard, cloudId }: SessionDataType): void {
	const config = fg('ufo-use-statsig-config-confluence-new')
		? getConfig('confluence_ufo_config')
		: FALLBACK_UFO_CONFIG;

	if (!config?.enabled) {
		return;
	}

	configureResourceTimings(resourceTimingsConfig);
	startResourceTimingBuffer();

	runConfigureSsrTiming();

	initUFOTrace();

	const perfPushExperienceRates = {
		'view-page': 1,
		'edit-page': 1,
		'live-edit': 1,
		'space-overview': 1,
		'confluence-home': 1,
		'advanced-search': 1,
		'unified-search': 1,
		'embedded-view-page': 1,
		'embedded-live-edit': 1,
		'edit-page-embed': 1,
		'whiteboard-view-page': 1,
		'database-view-page': 1,
		'public-link-shared-page': 1,
	};

	const assetsConfig: AssetsConfig = {
		classification: {
			all: true,
			allAtlassian: ({ entry }: AssetsConfigAllAtlassianArgs) =>
				entry.name.includes('atl-paas.net/') ||
				(entry.name.includes('cloudfront') && entry.name.includes('batch.js')),
			preloaded: ({ entry, SSRDoneTime }: AssetsConfigPreloadedArgs) =>
				Boolean(SSRDoneTime && entry.startTime <= SSRDoneTime),
		},
	};

	init(getAnalyticsWebClient() as Promise<GenericAnalyticWebClientPromise>, {
		postInteractionLog: {
			enabled: true,
			rates: perfPushExperienceRates,
		},
		extraInteractionMetrics: {
			enabled: true,
			rates: perfPushExperienceRates,
		},
		enableSegmentHighlighting: false,
		enableAdditionalPerformanceMarks: false,
		shouldCalculateLighthouseMetricsFromTTAI: true,
		manuallyTrackReactProfilerMounts: true,
		product: 'confluence',
		population: StatsigConfigurations.getString('cc_ufo_group', 'unknown'),
		region: shard || 'unknown',
		getReactHydrationStats: fg('confluence_frontend_enable_hydration_stats')
			? getReactHydrationStats
			: undefined,
		...config,
		// vc config must come after ...config spread to prevent the Statsig dynamic config
		// from overwriting locally-controlled settings (e.g. enabledVCRevisions).
		vc: {
			...config?.vc,
			enabled: true,
			heatmapSize: 200,
			oldDomUpdates: false,
			selectorConfig: {
				id: false,
				role: false,
				className: true,
				testId: true,
			},
			devToolsEnabled: StatsigConfigurations.isKillSwitchOn('confluence_enable_ttvc_devtool'),
			enabledVCRevisions: {
				all: fg('ufo_disable_ttvc_v4') ? [] : ['fy26.04'],
			},
			includeSSRInV3: true,
			includeSSRRatio: true,
			trackLayoutShiftOffenders: fg('confluence_vc_track_layout_shift_offenders'),
		},
		minorInteractions: ['space-navigation-hover', 'hover-page-card'],
		ssr: { getSSRDoneTime, getSSRTimings },
		additionalPayloadData: (interaction) =>
			getAdditionalPayloadData(interaction, {
				cloudId,
				isShardPreProd: shard ? shard.startsWith('conf-prod-us-preprod') : false,
				layoutShiftWithAttribution: getLayoutShiftWithAttribution(
					interaction.ufoName,
					getConfig('confluence_cls_attribution_config'),
				),
			}),
		extraSearchPageInteraction: {
			enabled: true,
			searchPageMetricName: 'unified_search',
			searchPageRoute: '/wiki/search',
		},
		enableBetterPageVisibilityApi: true,
		assetsConfig,
	});

	// When client-side TTVC calculation is disabled, the __on_ufo_vc_debug_data_ready callback
	// is never called (no VC calculators run). Listen for UFO_FINISH_INTERACTION instead to
	// ensure __TTVC_READY__ is set and Apollo stats are still reported on interaction completion.
	if (typeof window !== 'undefined') {
		window.addEventListener(
			'UFO_FINISH_INTERACTION',
			() => {
				if (!fg('ufo_disable_ttvc_v4')) {
					return;
				}
				reportApolloDiffStoreStats();

				// @ts-ignore
				window.__TTVC_READY__ = true;
			},
			{ once: true },
		);
	}
}

// Helper: report Apollo diffStore stats to monitoring
function reportApolloDiffStoreStats() {
	const apolloStats = window['__APOLLO_STATS__'] as { diffStore: Record<string, number> };
	if (apolloStats?.diffStore) {
		const monitoringClient = getMonitoringClient();
		const entries = Object.entries(apolloStats?.diffStore)
			.sort((a, b) => b[1] - a[1])
			.slice(0, 10);
		for (const [query, value] of entries) {
			// cc_fe_telemetry_apolloDiffStore in SFX
			monitoringClient.gauge(`apolloDiffStore`, value, [`query:${query}`]);
		}
	}
}

// UFO library emits mutation debug data once it is available. Debug data includes all mutations listed in the UFO dev tools,
// as well as includes a UFO traceId that can be linked to UFO (BM) page load event

// https://hello.atlassian.net/wiki/spaces/APD/pages/5398802024/RUNBOOK+Emitting+and+receiving+TTVC+debug+data+from+UFO
if (typeof window !== 'undefined') {
	// @ts-ignore
	window.__on_ufo_vc_debug_data_ready = function (data: VCRevisionDebugDetails) {
		// only send debug data when revision is in the allowed list
		const allowedRevisionList = StatsigConfigurations.getStringList(
			'confluence_debug_data_revision_config',
			[],
		);

		if (
			data?.revision &&
			data?.isClean &&
			allowedRevisionList.includes(data.revision) &&
			fg('confluence_emit_v3_debug_data')
		) {
			const payload = {
				source: 'confluenceRoot',
				action: 'emitted',
				actionSubject: 'debugDataV3',
				attributes: {
					...data,
				},
				eventType: 'operational',
			};
			void getAnalyticsWebClient().then(
				(analyticsClient: { sendOperationalEvent: (payload: any) => void }) => {
					analyticsClient.sendOperationalEvent(payload);
				},
			);
		}

		// When TTVC v4 client-side calculation is active (flag off), Apollo stats fire here.
		// When it's disabled (flag on), they fire via UFO_FINISH_INTERACTION instead.
		if (!fg('ufo_disable_ttvc_v4')) {
			reportApolloDiffStoreStats();
		}

		// This is to inform external tools/tests that page finished loading and "performance measured" events were emitted.
		// @ts-ignore
		window.__TTVC_READY__ = true;
	};
}
